import {Component, ElementRef, Input, OnChanges, OnInit, ViewChild} from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5percent from "@amcharts/amcharts5/percent";
import * as am5themeDark from "@amcharts/amcharts5/themes/Dark"
import {ToggleDarkModeService} from "../../Shared/toggle-dark-mode.service";
import {DarkTheme} from "@amcharts/amcharts5/.internal/themes/DarkTheme";
import {color, Root} from "@amcharts/amcharts5";
import { THREAT_COLORS } from '../../Shared/threat-colors';
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";

interface ThreatData {
    name: string;
    friendly: string;
    count: number;
    // ...other fields if needed
}

@Component({
    selector: 'app-donut-chart',
    templateUrl: './donut-chart.component.html',
    styleUrls: ['./donut-chart.component.css'],
    standalone: false
})
export class DonutChartComponent implements OnInit, OnChanges {
    @Input() total_threat_data: any;
    @ViewChild('chartdiv', { static: true }) chartDiv!: ElementRef<HTMLDivElement>;
    isDarkTheme = false;
    root: any;
    chart: any;
    series: any;
    modal: any;
    first_launch = true;
    private temp_data: any;

    constructor(private helper: ToggleDarkModeService) {
    }

    ngOnInit() {
        this.root = am5.Root.new(this.chartDiv.nativeElement);
        this.modal = am5.Modal.new(this.root, {
            content: "No data available<p>Select different timeline</p>",

        });
        this.helper.selected_mode$.subscribe(mode => {
            this.isDarkTheme = mode;
            this.change_theme(this.root);
            if (!this.first_launch) this.reloadData();
        });
        this.loadData();
    }

    ngOnChanges() {
        if (!this.first_launch) this.reloadData();
    }

    private loadData() {
        this.chart = this.root.container.children.push(am5percent.PieChart.new(this.root, {
            layout: this.root.verticalLayout,
            innerRadius: 70,
            outerRadius: 75
        }));


        this.series = this.chart.series.push(am5percent.PieSeries.new(this.root, {
                valueField: "count",
                categoryField: "friendly",
            })
        );

        this.setColors();

        const exporting = am5plugins_exporting.Exporting.new(this.root, {
            menu: am5plugins_exporting.ExportingMenu.new(this.root, {})
        });

        // Slice labels — abbreviated category name, auto-hidden on collision by amCharts
        this.series.labels.template.setAll({
            fontSize: "11px",
            text: "{category}"
        });

        // sector name, count, and percentage
        this.series.slices.template.set(
            "tooltipText",
            "{category}\nCount: {value.formatNumber('#,###')}  ({valuePercentTotal.formatNumber('#.#')}%)"
        );

        // brighten the slice
        this.series.slices.template.states.create("hover", {
            scale: 1.04
        });

        if (this.total_threat_data.length <= 0) {
            this.modal.open();
        } else {
            if (this.modal) this.modal.close();
        }
        if (this.total_threat_data.length >= 11) {
            this.temp_data = this.total_threat_data.slice(0, 10);
        } else {
            this.temp_data = this.total_threat_data;
        }
        this.series.data.setAll(this.temp_data);
        this.series.appear(2000, 500);
        this.first_launch = false;
    }


    private change_theme(root: Root) {
        if (this.isDarkTheme) {
            root.setThemes([
                am5themes_Animated.new(root),
                DarkTheme.new(root)
            ]);
        } else {
            root.setThemes([
                am5themes_Animated.new(root)
            ]);
        }
    }

    private setColors() {
        if (this.total_threat_data && this.total_threat_data.length > 0) {
            // Sort the total data by frequency (count) descending
            const sortedData = (this.total_threat_data as ThreatData[]).slice().sort(
                (a: ThreatData, b: ThreatData) => b.count - a.count
            );
            // If you want a maximum of 10 entries:
            this.temp_data = sortedData.length >= 11 ? sortedData.slice(0, 10) : sortedData;

            // Create a ColorSet based on the sorted data.
            const colorsArray = this.temp_data.map((item: ThreatData) => THREAT_COLORS[item.name] || am5.color(0x999999));
            const colorSet = am5.ColorSet.new(this.root, {
                colors: colorsArray,
                passOptions: {}
            });
            this.series.set("colors", colorSet);
        }
    }

    private reloadData() {
        if (this.total_threat_data.length <= 0) {
            this.modal.open();
        } else {
            if (this.modal) this.modal.close();
        }
        this.setColors();
        this.series.data.setAll(this.total_threat_data);
        this.series.appear(2000, 500);

    }
}
